import type { PackClass, PackWord } from '@/data/packs/types';
import { wordInClass } from '@/data/packs/membership';

/**
 * Generic predicate that decides whether a pop on a given word counts as a hit.
 * The engine never knows what "noun" or "verb" means — only that words have a `class`
 * and the rule picks one.
 *
 * `isCandidate` (optional) lets a rule restrict the spawn pool to a subset of the set —
 * e.g. classifier mode only flies classifier words; the bannered noun never becomes a target.
 * Defaults to "everything is a candidate" so MatchClass works without overriding it.
 */
export interface BoardRule {
  readonly classId: string;
  readonly prompt: string;
  /** Optional pitch-contour glyph name for the banner (tonal packs). See game/contours.ts. */
  readonly contour?: string;
  matches(word: PackWord): boolean;
  isCandidate?(word: PackWord): boolean;

  // ── picture modes (specs/picture-word-mode.md) ────────────────────────────────
  // A picture rule's `matches` is a *per-instant* predicate — only the word currently in the banner
  // counts — so the board needs two extra questions answered that a class rule answers implicitly.

  /**
   * Word id whose picture the banner currently shows, or '' when the queue is spent. `undefined`
   * (the default for every class rule) means "this is a text banner", and is the flag BoardScene
   * branches on.
   */
  readonly iconId?: string;
  /**
   * Will this word be a target at some point during THIS board? Used for spawn sampling and the
   * remaining-target count, where `matches` is too narrow. Defaults to `matches`, so class-match
   * boards sample and complete exactly as before.
   */
  willBeTarget?(word: PackWord): boolean;
  /**
   * Called when a target word leaves the board for ANY reason — smashed correctly, tapped while a
   * different picture was showing, dropped by the crumble, or eaten by the ladybird. Drops it from
   * the remaining queue so the banner never asks for a word that is no longer on screen.
   */
  resolve?(word: PackWord): void;

  // ── sentence order (specs/sentence-order-mode.md) ─────────────────────────────
  // The board draws a slot strip along the bottom instead of leaving the answer implicit, and it
  // needs two things from the rule that no other mode has: what the slots are called, and whether a
  // wrong tap may destroy the tile it hit.

  /**
   * One label per slot the player has to fill ('Verb', 'Artikel', 'Nomen'), in order. `undefined`
   * — the default for every other rule — means "this board has no slot strip", and is the flag
   * BoardScene branches on.
   */
  readonly slotRoles?: string[];
  /** Text of the pre-filled first slot ('Ich', 'Das Znüni'), or '' when the strip starts empty. */
  readonly slotVorgabe?: string;
  /**
   * When true a wrong tap costs a life but leaves the tile on the board. Every other mode consumes
   * whatever was tapped, which on a sentence board would delete a word the sentence still needs and
   * leave the strip with a hole. See specs/sentence-order-mode.md §4.
   */
  readonly keepsTilesOnWrongTap?: boolean;
  /** The whole sentence, read aloud once the last slot is filled. */
  readonly fullSentence?: string;
}

/** Stable identity for a word: its id when it has one, else its surface form. */
function wordId(w: PackWord): string {
  return typeof w.id === 'string' ? w.id : w.text;
}

export class MatchClass implements BoardRule {
  readonly classId: string;
  readonly prompt: string;
  readonly contour?: string;

  constructor(cls: PackClass) {
    this.classId = cls.id;
    this.prompt = cls.prompt;
    this.contour = cls.contour;
  }

  matches(word: PackWord): boolean {
    return wordInClass(word, this.classId);
  }
}

/**
 * Picture-word rule: the banner shows one target word's picture and only that word is correct.
 * Smash it and the banner moves to the next picture, so one board asks as many questions as it has
 * targets. See specs/picture-word-mode.md §2.
 *
 * The queue is consumed from the front, so there is no separate "advance" step: `resolve()` removes
 * a word wherever it sits, and whatever is left at the front becomes the picture. That single
 * operation covers all four ways a target can leave the board, including the awkward one — being
 * tapped while a *different* picture is showing. That tap is wrong (it costs a life), but the word
 * is gone from the screen, so it must also leave the queue or the board would later show a picture
 * with no matching tile and become unfinishable.
 *
 * `classId` is the board's topic, which is what the "?" help overlay explains — the picture is the
 * prompt, so `prompt` is only used by the §2.4 fallback and by Survival.
 */
export class MatchPictureWord implements BoardRule {
  readonly classId: string;
  readonly prompt: string;
  /** Every word that is a target this board — fixed at construction, never consumed. */
  private readonly targetIds: ReadonlySet<string>;
  /** Targets not yet resolved, in order. The front one is the picture. */
  private queue: PackWord[];

  constructor(cls: PackClass, targets: PackWord[]) {
    this.classId = cls.id;
    this.prompt = cls.prompt;
    this.queue = [...targets];
    this.targetIds = new Set(targets.map(wordId));
  }

  get iconId(): string {
    return this.queue.length > 0 ? wordId(this.queue[0]) : '';
  }

  matches(word: PackWord): boolean {
    return this.queue.length > 0 && wordId(word) === wordId(this.queue[0]);
  }

  willBeTarget(word: PackWord): boolean {
    return this.targetIds.has(wordId(word));
  }

  resolve(word: PackWord): void {
    const id = wordId(word);
    const i = this.queue.findIndex((w) => wordId(w) === id);
    if (i >= 0) this.queue.splice(i, 1);
  }

  /** Targets still to be asked. Exposed for tests and for board-completion assertions. */
  get remaining(): number {
    return this.queue.length;
  }
}

/**
 * Banner-driven rule for classifier / particle / similar modes.
 *
 * Per-board the engine picks one context word (noun, sentence, …) and renders its `banner`
 * (or plain `text`) as the prompt. Flying targets are all words of `candidateClassId`; a target
 * matches when its `forKey` metadata field — a CSV of context ids — contains the picked context's
 * `id` (or, as a fallback, its `text`).
 *
 * Implements `MatchClassifierForNoun` from multilingual-thai.md §5 and the equivalent particle
 * rules from multilingual-japanese.md / multilingual-korean.md, all with one class.
 */
export class MatchTagForBannerContext implements BoardRule {
  readonly classId: string;
  readonly prompt: string;
  private readonly contextId: string;
  private readonly forKey: string;

  constructor(opts: {
    candidateClassId: string;
    contextWord: PackWord;
    bannerText: string;
    forKey: string;
  }) {
    this.classId = opts.candidateClassId;
    this.prompt = opts.bannerText;
    this.forKey = opts.forKey;
    const id = opts.contextWord.id;
    this.contextId = typeof id === 'string' ? id : opts.contextWord.text;
  }

  matches(word: PackWord): boolean {
    if (!wordInClass(word, this.classId)) return false;
    const raw = word[this.forKey];
    if (typeof raw !== 'string') return false;
    return raw.split(',').map((s) => s.trim()).includes(this.contextId);
  }

  isCandidate(word: PackWord): boolean {
    return wordInClass(word, this.classId);
  }
}

/**
 * Sentence-order rule: the board holds one sentence, and only the word belonging in the next empty
 * slot is correct. See specs/sentence-order-mode.md §2.
 *
 * This is `MatchPictureWord`'s queue with the front pinned. There the queue may lose its head to a
 * mis-tap and the board stays playable — the banner simply asks a different question. Here the
 * queue IS the answer, so `resolve()` advances on the head and on nothing else: a target tapped out
 * of turn is wrong, costs a life, and leaves both the queue and (via `keepsTilesOnWrongTap`) the
 * board untouched.
 *
 * That head-only rule also makes the two paths BoardScene drives feel right without a special case.
 * A tile lost to the crumble is passed to `resolve()` like any other departing target: if it was the
 * head the sentence moves on, if it was not the queue holds still and the board simply ends a token
 * short — the life the crumble already charged is the penalty, and nothing is left unfinishable.
 */
export class MatchNextInSentence implements BoardRule {
  readonly classId: string;
  readonly prompt: string;
  readonly slotRoles: string[];
  readonly slotVorgabe: string;
  readonly fullSentence: string;
  readonly keepsTilesOnWrongTap = true;
  /**
   * One entry per slot, each a list of tile ids that slot ACCEPTS. Usually one — but some slots
   * genuinely have more than one right answer, and refusing them would punish a correct child.
   * The case that forced this: "Je suis fatigué" / "Yo estoy cansado" / "Io sono stanco". `Je`,
   * `Yo` and `Io` carry no grammatical gender, so the feminine form is equally correct — a girl
   * agreeing the adjective to herself was being marked wrong. Both forms are now accepted, which
   * is better than the first fix (dropping the feminine tile), because she still gets to build
   * her own sentence. Encoded in `folge` as `id-a|id-b`.
   */
  private readonly order: string[][];
  private readonly targetIds: ReadonlySet<string>;
  /** How many slots are filled — the index of the only tile that is correct right now. */
  private placed = 0;

  constructor(opts: {
    candidateClassId: string;
    prompt: string;
    order: string[];
    roles: string[];
    vorgabe: string;
    sentence: string;
  }) {
    this.classId = opts.candidateClassId;
    this.prompt = opts.prompt;
    this.order = opts.order.map((slot) => slot.split('|').map((x) => x.trim()).filter(Boolean));
    this.slotRoles = [...opts.roles];
    this.slotVorgabe = opts.vorgabe;
    this.fullSentence = opts.sentence;
    this.targetIds = new Set(this.order.flat());
  }

  matches(word: PackWord): boolean {
    return this.placed < this.order.length && this.order[this.placed].includes(wordId(word));
  }

  /** Only tiles fly; the sentence itself is data for the strip, never a target. */
  isCandidate(word: PackWord): boolean {
    return wordInClass(word, this.classId);
  }

  willBeTarget(word: PackWord): boolean {
    return this.targetIds.has(wordId(word));
  }

  resolve(word: PackWord): void {
    if (this.matches(word)) this.placed += 1;
  }

  /** Slots still to fill. Exposed for tests and board-completion assertions. */
  get remaining(): number {
    return this.order.length - this.placed;
  }
}
